import {
  LeftOutlined,
  RightOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined,
} from '@ant-design/icons';
import type { AgGridReact } from 'ag-grid-react';
import { Dropdown, Space, theme } from 'antd';
import { RefObject, useState, useEffect, useMemo, useCallback } from 'react';

import { Tooltip } from 'components/Tooltip';
import { Button } from 'components/general/Button';

/** Custom pagination state for group-based or external pagination */
export interface CustomPaginationState {
  currentPage: number; // 0-indexed
  totalItems: number;
  onPageChange: (page: number) => void;
}

interface TableControlsProps {
  gridRef: RefObject<AgGridReact | null>;
  showZoomControls?: boolean;
  scale: number;
  onScaleChange: (scale: number) => void;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  paginationPageSizeSelector: number[];
  pagination?: boolean;
  /** When provided, uses custom pagination instead of AG Grid's API */
  customPagination?: CustomPaginationState;
}

export function TableControls({
  gridRef,
  showZoomControls = true,
  scale,
  onScaleChange,
  pageSize,
  onPageSizeChange,
  paginationPageSizeSelector,
  pagination,
  customPagination,
}: TableControlsProps) {
  const { token } = theme.useToken();
  const [gridPaginationData, setGridPaginationData] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRows: 0,
    currentPageSize: pageSize,
    displayedRows: '',
  });

  // Update pagination data when grid changes (only when not using custom pagination)
  useEffect(() => {
    if (customPagination) return; // Skip if using custom pagination

    const updatePagination = () => {
      const gridApi = gridRef?.current?.api;
      if (!gridApi) return;

      const currentPage = gridApi.paginationGetCurrentPage() + 1;
      const totalPages = gridApi.paginationGetTotalPages();
      const totalRows = gridApi.paginationGetRowCount();
      const currentPageSize = gridApi.paginationGetPageSize();

      const displayedRows =
        totalRows === 0
          ? ''
          : (() => {
              const rowStart = (currentPage - 1) * currentPageSize + 1;
              const rowEnd = Math.min(currentPage * currentPageSize, totalRows);
              return `${rowStart} to ${rowEnd} of ${totalRows}`;
            })();

      setGridPaginationData({
        currentPage,
        totalPages,
        totalRows,
        currentPageSize,
        displayedRows,
      });
    };

    // Set up a timer to periodically update pagination data
    // This ensures we capture pagination changes even if events don't fire properly
    const intervalId = setInterval(updatePagination, 100);

    // Initial update
    updatePagination();

    return () => {
      clearInterval(intervalId);
    };
  }, [gridRef, customPagination]);

  // Compute pagination data - use custom if provided, otherwise use grid data
  const paginationData = useMemo(() => {
    if (customPagination) {
      const { currentPage, totalItems, onPageChange: _ } = customPagination;
      // Guard against invalid pageSize (0 or negative) to prevent Infinity/NaN
      const safePageSize = pageSize > 0 ? pageSize : 1;
      const totalPages = Math.max(1, Math.ceil(totalItems / safePageSize));
      const displayPage = currentPage + 1; // Convert to 1-indexed for display
      const rowStart = totalItems > 0 ? currentPage * safePageSize + 1 : 0;
      const rowEnd = Math.min((currentPage + 1) * safePageSize, totalItems);
      const displayedRows =
        totalItems === 0 ? '' : `${rowStart} to ${rowEnd} of ${totalItems}`;

      return {
        currentPage: displayPage,
        totalPages,
        totalRows: totalItems,
        currentPageSize: safePageSize,
        displayedRows,
      };
    }
    return gridPaginationData;
  }, [customPagination, pageSize, gridPaginationData]);

  const zoomOptions = useMemo(
    () => [
      { value: 0.25, label: '25%' },
      { value: 0.5, label: '50%' },
      { value: 0.75, label: '75%' },
      { value: 1, label: '100%' },
      { value: 1.25, label: '125%' },
      { value: 1.5, label: '150%' },
      { value: 2, label: '200%' },
      { value: 3, label: '300%' },
      { value: 4, label: '400%' },
    ],
    []
  );

  const zoomOptionsMap = useMemo(() => {
    return zoomOptions.reduce(
      (acc, item) => {
        acc[item.value] = item.label;
        return acc;
      },
      {} as Record<number, string>
    );
  }, [zoomOptions]);

  const scaleValue = useMemo(() => {
    return zoomOptionsMap[scale] ?? `${Math.round(scale * 100)}%`;
  }, [scale, zoomOptionsMap]);

  const handleScaleIncrease = () => {
    const currentIndex = zoomOptions.findIndex((item) => item.value === scale);
    if (currentIndex !== -1 && currentIndex < zoomOptions.length - 1) {
      onScaleChange(zoomOptions[currentIndex + 1].value);
    }
  };

  const handleScaleDecrease = () => {
    const currentIndex = zoomOptions.findIndex((item) => item.value === scale);
    if (currentIndex !== -1 && currentIndex > 0) {
      onScaleChange(zoomOptions[currentIndex - 1].value);
    }
  };

  // Navigation functions - use custom pagination if provided
  const goToFirstPage = useCallback(() => {
    if (customPagination) {
      customPagination.onPageChange(0);
    } else {
      gridRef?.current?.api?.paginationGoToFirstPage();
    }
  }, [gridRef, customPagination]);

  const goToPreviousPage = useCallback(() => {
    if (customPagination) {
      customPagination.onPageChange(
        Math.max(0, customPagination.currentPage - 1)
      );
    } else {
      gridRef?.current?.api?.paginationGoToPreviousPage();
    }
  }, [gridRef, customPagination]);

  const goToNextPage = useCallback(() => {
    if (customPagination) {
      const safePageSize = pageSize > 0 ? pageSize : 1;
      const totalPages = Math.ceil(customPagination.totalItems / safePageSize);
      customPagination.onPageChange(
        Math.min(totalPages - 1, customPagination.currentPage + 1)
      );
    } else {
      gridRef?.current?.api?.paginationGoToNextPage();
    }
  }, [gridRef, customPagination, pageSize]);

  const goToLastPage = useCallback(() => {
    if (customPagination) {
      const safePageSize = pageSize > 0 ? pageSize : 1;
      const totalPages = Math.ceil(customPagination.totalItems / safePageSize);
      customPagination.onPageChange(totalPages - 1);
    } else {
      gridRef?.current?.api?.paginationGoToLastPage();
    }
  }, [gridRef, customPagination, pageSize]);

  const leftNavButtons = useMemo(
    () => [
      {
        onClick: goToFirstPage,
        disabled: paginationData.currentPage === 1,
        icon: DoubleLeftOutlined,
      },
      {
        onClick: goToPreviousPage,
        disabled: paginationData.currentPage === 1,
        icon: LeftOutlined,
      },
    ],
    [goToFirstPage, goToPreviousPage, paginationData.currentPage]
  );

  const rightNavButtons = useMemo(
    () => [
      {
        onClick: goToNextPage,
        disabled: paginationData.currentPage === paginationData.totalPages,
        icon: RightOutlined,
      },
      {
        onClick: goToLastPage,
        disabled: paginationData.currentPage === paginationData.totalPages,
        icon: DoubleRightOutlined,
      },
    ],
    [
      goToNextPage,
      goToLastPage,
      paginationData.currentPage,
      paginationData.totalPages,
    ]
  );

  const items = zoomOptions.map((item) => ({
    key: String(item.value),
    label: item.label,
  }));

  return (
    <div
      className="flex items-center justify-between px-3 py-2 shrink-0 min-h-[40px]"
      style={{
        backgroundColor: token.colorBgElevated,
        borderColor: token.colorBorder,
      }}
    >
      {showZoomControls && (
        <div className="flex items-center gap-2">
          <span
            className="text-sm font-medium"
            style={{ color: token.colorText }}
          >
            Zoom:
          </span>

          <Space.Compact>
            <Tooltip title="Zoom out">
              <Button size="small" onClick={handleScaleDecrease}>
                -
              </Button>
            </Tooltip>

            <Dropdown
              menu={{
                items,
                onClick: ({ key }) => {
                  const scaleValue = zoomOptionsMap[Number(key)];
                  if (scaleValue) {
                    onScaleChange(Number(key));
                  }
                },
              }}
            >
              <Button size="small">{scaleValue}</Button>
            </Dropdown>

            <Tooltip title="Zoom in">
              <Button size="small" onClick={handleScaleIncrease}>
                +
              </Button>
            </Tooltip>
          </Space.Compact>
        </div>
      )}

      {pagination !== false && (
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm" style={{ color: token.colorText }}>
              Page Size:
            </span>
            <select
              className="text-sm cursor-pointer rounded px-1.5 py-0.5 border"
              style={{
                backgroundColor: token.colorBgContainer,
                color: token.colorText,
                borderColor: token.colorBorder,
              }}
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
            >
              {Array.isArray(paginationPageSizeSelector) &&
                paginationPageSizeSelector.length > 0 &&
                paginationPageSizeSelector.map((size: number) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}

              {(!Array.isArray(paginationPageSizeSelector) ||
                paginationPageSizeSelector.length === 0 ||
                !paginationPageSizeSelector.includes(pageSize)) && (
                <option value={pageSize}>{pageSize}</option>
              )}
            </select>
          </div>

          <div
            className="flex items-center gap-2"
            style={{ color: token.colorText }}
          >
            <span className="text-sm">{paginationData.displayedRows}</span>
            <div className="flex gap-1">
              {/* eslint-disable-next-line */}
              {leftNavButtons.map(
                ({ onClick, disabled, icon: Icon }, index) => (
                  <button
                    key={index}
                    onClick={onClick}
                    disabled={disabled}
                    className="bg-transparent border-none p-1 flex items-center justify-center disabled:cursor-not-allowed cursor-pointer"
                    style={{
                      color: disabled
                        ? token.colorTextDisabled
                        : token.colorText,
                    }}
                  >
                    <Icon />
                  </button>
                )
              )}
            </div>

            <span className="py-0.5 text-sm">
              Page {paginationData.currentPage} of {paginationData.totalPages}
            </span>

            {/* eslint-disable-next-line */}
            {rightNavButtons.map(({ onClick, disabled, icon: Icon }, index) => (
              <button
                key={index}
                onClick={onClick}
                disabled={disabled}
                className="bg-transparent border-none p-1 flex items-center justify-center disabled:cursor-not-allowed cursor-pointer"
                style={{
                  color: disabled ? token.colorTextDisabled : token.colorText,
                }}
              >
                <Icon />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
